import { Observable } from 'rxjs';

// Opening an attachment: a file the browser can show by itself (image, PDF, plain text, audio/video)
// opens in a new window; any other file (Word, Excel, zip, …) is downloaded.

const VIEWABLE_TYPES_BY_EXTENSION: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  avif: 'image/avif',
  bmp: 'image/bmp',
  svg: 'image/svg+xml',
  pdf: 'application/pdf',
  txt: 'text/plain;charset=utf-8',
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  wav: 'audio/wav',
  mp4: 'video/mp4',
  webm: 'video/webm',
  mov: 'video/quicktime',
};

function extensionOf(filename: string): string {
  const match = /\.([a-z0-9]+)$/i.exec(String(filename ?? '').trim());
  return match ? match[1].toLowerCase() : '';
}

/** The type to show the file with in a browser window, or null when the browser can't show it.
 * The file name decides first: a stored mime type is often missing or application/octet-stream. */
export function viewableMimeType(filename: string, mimeType?: string | null): string | null {
  const byExtension = VIEWABLE_TYPES_BY_EXTENSION[extensionOf(filename)];
  if (byExtension) return byExtension;
  const type = String(mimeType ?? '').toLowerCase().split(';')[0].trim();
  if (!type) return null;
  if (type === 'application/pdf') return type;
  if (type === 'text/plain') return 'text/plain;charset=utf-8';
  if (/^(image|audio|video)\//.test(type) && type !== 'image/tiff' && type !== 'image/heic') return type;
  return null;
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoked later: Safari may still be reading it after click() returns.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Opens the attachment in a new window when the browser can show it, otherwise downloads it.
 * Must be called from the click handler: the window is opened right away (one opened after the async
 * fetch would be blocked as a popup) and filled when the file arrives. The file needs the auth header,
 * so it is shown via a blob URL.
 */
export function openOrDownloadAttachment(
  filename: string,
  mimeType: string | null | undefined,
  fetchFile: () => Observable<Blob>,
  onError: (err: unknown) => void,
): void {
  const viewType = viewableMimeType(filename, mimeType);
  if (!viewType) {
    fetchFile().subscribe({ next: (blob) => downloadBlob(blob, filename), error: onError });
    return;
  }

  const win = window.open('', '_blank');
  if (win) {
    try {
      win.document.title = filename;
      win.document.body.style.margin = '0';
      win.document.body.style.fontFamily = 'system-ui, sans-serif';
      win.document.body.textContent = 'Loading…';
    } catch {
      // Cross-origin about:blank in some browsers — the window still works.
    }
  }
  fetchFile().subscribe({
    next: (blob) => {
      // Give the blob the right type so the browser shows it instead of downloading it.
      const typed = blob.type && blob.type.split(';')[0] === viewType.split(';')[0] ? blob : new Blob([blob], { type: viewType });
      const url = URL.createObjectURL(typed);
      if (win && !win.closed) {
        win.location.href = url;
      } else if (!window.open(url, '_blank')) {
        downloadBlob(typed, filename);
        return;
      }
      // Long enough for the new window to load it.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
    error: (err) => {
      win?.close();
      onError(err);
    },
  });
}
